import caseImage from 'assets/hong-kong-dial/case.jpg'
import galleryImage1 from 'assets/hong-kong-dial/gallery/1.jpg'
import galleryImage2 from 'assets/hong-kong-dial/gallery/2.jpg'
import galleryImage3 from 'assets/hong-kong-dial/gallery/3.jpg'
import galleryImage4 from 'assets/hong-kong-dial/gallery/4.jpg'
import galleryImage5 from 'assets/hong-kong-dial/gallery/5.jpg'
import galleryImage6 from 'assets/hong-kong-dial/gallery/6.jpg'
import galleryImage7 from 'assets/hong-kong-dial/gallery/7.jpg'
import originImage from 'assets/hong-kong-dial/origin.jpg'
import quartzImage from 'assets/hong-kong-dial/quartz.jpg'
import watchfaceImage from 'assets/hong-kong-dial/watchface.png'
import watchfacesImage from 'assets/hong-kong-dial/watchfaces.jpg'
import Head from 'next/head'
import Image from 'next/image'

import Footer from 'components/hong-kong-dial/Footer'
import TAImage from 'components/hong-kong-dial/Image'
import ImageCarousel from 'components/hong-kong-dial/ImageCarousel'
import Spacer from 'components/hong-kong-dial/Spacer'
import Text from 'components/hong-kong-dial/Text'

import Logos from '../../assets/hong-kong-dial/logos.svg'
import { LogosWrapper, Masthead, Root } from './hong-kong-dial.style'

const description =
  'In January 2023, The Armoury’s Creative Team went on an expedition through Europe visiting our favourite suppliers at their respective headquarters. On our journey through Scotland, we made a stop in Glasgow to catch up with anOrdain and Paulin. We were particularly smitten by a yet-to-be-released 1970’s style C-shaped case in a charming 34mm diameter and we wanted to make a special Armoury version of this very attractive and wearable watch. Thus was born the Hong Kong Dial, a riff on the classic California Dial configuration of half-and-half Roman and Arabic numerals, and a very appropriate combination given The Armoury’s Eastern and Western background.'

export default function Page() {
  return (
    <>
      <Head>
        <title>Hong Kong Dial - The Armoury</title>
        <meta name="description" content={description} />
        {/* Facebook Meta Tags */}
        <meta property="og:url" content="https://www.thearmoury.com/watches/hong-kong-dial" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Hong Kong Dial" />
        <meta property="og:description" content={description} />
        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="watches.thearmoury.com" />
        <meta property="twitter:url" content="https://www.thearmoury.com/watches/hong-kong-dial" />
        <meta name="twitter:title" content="Hong Kong Dial" />
        <meta name="twitter:description" content={description} />

        <link rel="icon" type="image/png" href="/favicon-32x32.png" sizes="32x32" />
        <link rel="icon" type="image/png" href="/favicon-16x16.png" sizes="16x16" />
        <link rel="manifest" type="image/png" href="/manifest.json" />
        <link rel="mast-icon" href="/safari-pinned-tab.svg" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link rel="stylesheet" href="https://use.typekit.net/dbw1jpz.css" />
      </Head>

      <Root>
        <Masthead>
          <Image src={watchfaceImage} alt="" width={872} height={842} />
          <LogosWrapper>
            <Logos />
          </LogosWrapper>
          <Text header="The Hong Kong Dial">
            <p>
              In January 2023, The Armoury’s Creative Team went on an expedition through Europe visiting our favourite
              suppliers at their respective headquarters. On our journey through Scotland, we made a stop in Glasgow to
              catch up with anOrdain and Paulin. We were particularly smitten by a yet-to-be-released 1970’s
              style C-shaped case in a charming 34mm diameter and we wanted to make a special Armoury version of this
              very attractive and wearable watch. Thus was born the Hong Kong Dial, a riff on the classic California
              Dial configuration of half-and-half Roman and Arabic numerals, and a very appropriate combination given
              The Armoury’s Eastern and Western background.
            </p>
          </Text>
        </Masthead>
        <TAImage src={originImage} />
        <Text header="The Origin">
          <p>
            The origins of the dial’s design actually hark back to Rolex’s earliest marketing of this style of dial
            which was known as “Error-Proof” but for various reasons is better known as a “California Dial”. These dials
            emphasized clear legibility using heavy graphics, such as the triangle at 12, and fonts, which matched well
            with Paulin’s bold graphic style. We adapted Chinese characters to suit these characteristics and
            were especially pleased to be able to use Chinese for the bottom half of the dial as 4, 5, 8 and 9 are
            visually the most interesting of the 1 - 12 required on a dial.
          </p>
          <p>
            For more info about the “Error-Proof” and “California Dial” history, please see -{' '}
            <a href="https://revolutionwatch.com/california-love/">https://revolutionwatch.com/california-love/</a>
          </p>
        </Text>
        <TAImage src={watchfacesImage} flush={true} />
        <Text header="Quartz!?">
          <p>
            We have a soft spot for thin, center-hand watches and one of the most economical and effective ways to
            achieve this is by using a quartz movement, which can be made thinner than a traditional manual wind
            movement. Furthermore, a quartz movement is convenient. Instead of having to wind the movement or re-setting
            the time when the watch has stopped, you simply change the battery every few years.
          </p>
        </Text>
        <TAImage src={quartzImage} />
        <Text header="Case Design">
          <p>
            We love working with the people at Paulin and anOrdain. Unusually for a watch company, they make
            little reference to classic watch design and instead progress in their own very distinct and quirky way with
            a heavy emphasis on graphic elements.
          </p>
          <p>
            Their case design reflects their pragmatic, function-led approach to watchmaking. The black ring that
            surrounds the dial of the watch is a modular spacer that allows them to adapt the case for different sizes
            of movements while also adding an interesting design detail.
          </p>
        </Text>
        <TAImage src={caseImage} />
        <Text header="Colour — How to wear it">
          <p>
            The black dial is actually more like a matte charcoal with dark gold numerals. On the silver mesh bracelet,
            it lends itself to almost any outfit. Its compact size suits many wrists for both men and women. The
            coloured dial variations of our Modul A are great for adding a sharp pop of colour. For instance, the yellow
            dial with a navy summer blazer. Or, you could further emphasize an existing colour in the palette of your
            outfit, such as using the baby blue dial with a checked jacket that contained some blue within its pattern.
          </p>
        </Text>
        <ImageCarousel
          images={[
            galleryImage1,
            galleryImage2,
            galleryImage3,
            galleryImage4,
            galleryImage5,
            galleryImage6,
            galleryImage7,
          ]}
        />
        <Spacer />
        <Footer />
      </Root>
    </>
  )
}

Page.getLayout = function getLayout(page) {
  return <div className="overflow-x-hidden">{page}</div>
}
